// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { alpha } from '@mui/material/styles';

const styles = (theme: any) => ({
  cardWrapper: {
    position: 'relative',
    marginBottom: '3px',
  },
  // Lifted above the pinned controls band while being dragged up to a folder.
  cardDragging: {
    zIndex: 2,
  },
  cardSelected: {
    '& .MuiCard-root': {
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}33`,
    },
  },
  projectItem: {
    transition: theme.transitions.create(['box-shadow', 'margin-top']),
    '&:hover': {
      boxShadow: theme.shadows[15],
      marginTop: -0.5,
      '& .label': {
        opacity: 1,
        marginTop: 0,
      },
      '& .projectItemIcons': {
        opacity: 1,
        marginTop: 0,
      },
      '& .backdrop': {
        opacity: 0.7,
      },
    },
  },
  media: {
    height: 200,
    position: 'relative',
  },
  progress: {
    zIndex: 3001,
    position: 'absolute',
    width: '100%',
  },
  loadingBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
    zIndex: 3000,
    opacity: 0.5,
  },
  backdrop: {
    transition: theme.transitions.create('opacity'),
    backgroundColor: 'black',
    opacity: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  standardBackdrop: {
    opacity: 0.7,
  },
  // In-progress cards keep the design's dark image treatment with the pipeline stepper over
  // it; the body below stays identical to every other card.
  successBackdrop: {
    opacity: 0.9,
    background: 'linear-gradient(134.72deg, #000000 0%, #05071A 49.93%, #18216D 100%)',
  },
  progressPanel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
  },
  labelText: {
    color: '#fff',
    textAlign: 'center',
  },
  label: {
    transition: theme.transitions.create(['opacity', 'margin-top', 'color', 'background-color']),
    marginTop: -5,
    opacity: 0,
    color: 'white',
    padding: theme.spacing(2),
    border: '1px solid',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    '&:hover': {
      backgroundColor: 'white',
      color: 'black',
    },
  },
  // AUT-6432: the card body is four stacked lines from the Figma cards view - title, the
  // reference/created line, then est-delivery and due-date rows, the est-delivery one
  // carrying the status on its right.
  cardActions: {
    display: 'block',
    padding: '20px',
    position: 'relative',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    position: 'relative',
  },
  // A project's name reads the same everywhere it appears: 18px medium, matching the list row
  // and the map rail's cards. The cards Figma draws it at 16px semibold, but the two design
  // files disagree and this is the agreed one.
  titleText: {
    fontSize: '18px',
    fontWeight: 500,
    letterSpacing: '0.6857px',
    color: theme.palette.text.primary,
  },
  refLine: {
    fontSize: '12px',
    fontWeight: 400,
    letterSpacing: '0.5143px',
    color: theme.palette.text.primary,
    // The design fades this line rather than giving it its own grey.
    opacity: 0.7,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  metaText: {
    fontSize: '14px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  metaLabel: {
    fontWeight: 500,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: (color: string) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0,
  }),
  statusLabel: {
    fontSize: '12px',
    fontWeight: 400,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  projectItemIcons: {
    color: 'white',
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'margin-top', 'color', 'background-color']),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  deleteIcon: {
    color: 'white',
  },
  activeIcon: {
    color: theme.palette.secondary.main,
  },
  avatar: {
    position: 'absolute',
    top: '5%',
    left: '3%',
    background: '#ffffff',
    color: '#000000',
  },
  projectItemButton: {
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: 0,
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(),
    marginTop: theme.spacing(),
    '&:hover': {
      backgroundColor: 'white',
      color: 'black',
    },
  },
  cardText: {
    color: 'white',
  },
  cardTextWrapper: {
    textAlign: 'center',
    gridColumn: '1/3',
  },
  cardControls: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'grid',
    gridTemplateRows: '1fr 1fr',
    gridTemplateColumns: '1fr 1fr',
  },
  projectMenu: {
    border: '1px solid #FFFFFF',
    margin: theme.spacing(2),
    width: '30px',
    height: '30px',
  },
  moreProjectMenuIcon: {
    color: '#FFFFFF',
  },
  menuList: {
    minWidth: '150px',
  },
  fontWeight: {
    fontWeight: 300,
  },
  projectListItemPopper: {
    zIndex: 9999,
    left: '-5rem !important',
    '& .MuiPaper-root': {
      borderTopRightRadius: '20px',
      boxShadow: '0px 2px 12px 0px rgba(0,0,0,0.1)',
      '& .MuiButtonBase-root.MuiMenuItem-root:hover': {
        color: theme.palette.primary.main,
        fontWeight: 400,
      },
    },
  },
});

export default styles;
